import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { getActiveProfile, addGameResult } from '../profiles/ProfileManager';

const { width, height } = GAME_CONFIG;

interface MathQuestion {
  display: string;
  answer: number;
  options: number[];
}

type ChallengeType = 'math' | 'reading' | 'writing';

interface ChallengeState {
  type: ChallengeType;
  question: MathQuestion | null;
  attemptsLeft: number;
  challengesCompleted: number;
  totalChallenges: number;
  feedbackText?: Phaser.GameObjects.Text;
  feedbackBg?: Phaser.GameObjects.Graphics;
}

export class ChallengeScene extends Phaser.Scene {
  private state!: ChallengeState;
  private challengeTitle!: Phaser.GameObjects.Text;
  private challengeContent!: Phaser.GameObjects.Container;
  private feedbackContainer!: Phaser.GameObjects.Container;
  private progressText!: Phaser.GameObjects.Text;
  private challengeContainer!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'ChallengeScene' });
  }

  create(data?: { profileId?: string; score?: number; linesCleared?: number }): void {
    const profileId = data?.profileId ?? getActiveProfile()?.id;
    if (profileId === undefined) {
      this.scene.start('MenuScene');
      return;
    }

    this.state = {
      type: 'math',
      question: null,
      attemptsLeft: 3,
      challengesCompleted: 0,
      totalChallenges: 3,
    };

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a237e, 1);
    bg.fillRect(0, 0, width, height);

    // Decorative overlay
    bg.fillStyle(0x283593, 0.3);
    for (let i = 0; i < 50; i++) {
      bg.fillRect(
        Math.random() * width,
        Math.random() * height,
        1 + Math.random() * 3,
        1 + Math.random() * 3,
      );
    }

    // Title
    this.add.text(width / 2, 30, '🎓 ¡Desafío Educativo!', {
      fontSize: '24px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffcc80',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // Subtitle
    this.add.text(width / 2, 68, 'Responde correctamente para ganar +1 intento extra', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#b0bec5',
    }).setOrigin(0.5, 0);

    // Progress
    this.progressText = this.add.text(width / 2, 98, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5, 0);

    // Challenge content container
    this.challengeContainer = this.add.container(0, 0);

    // Feedback container
    this.feedbackContainer = this.add.container(0, 0).setDepth(20);

    this.nextChallenge();
  }

  private nextChallenge(): void {
    if (this.state.challengesCompleted >= this.state.totalChallenges) {
      // All challenges completed! Return to game
      this.returnToGame();
      return;
    }

    this.state.attemptsLeft = 2; // 2 attempts per challenge
    this.challengeContainer.removeAll(true);

    this.progressText.setText(
      `Desafío ${this.state.challengesCompleted + 1} de ${this.state.totalChallenges}`,
    );

    // Clean up any old feedback
    this.feedbackContainer.removeAll(true);

    // Pick random challenge type
    const types: ChallengeType[] = ['math', 'reading', 'writing'];
    this.state.type = types[Math.floor(Math.random() * types.length)];

    switch (this.state.type) {
      case 'math':
        this.renderMathChallenge();
        break;
      case 'reading':
        this.renderReadingChallenge();
        break;
      case 'writing':
        this.renderWritingChallenge();
        break;
    }
  }

  private getAgeRange(): { min: number; max: number } {
    const profile = getActiveProfile();
    const age = profile?.age ?? 7;

    if (age <= 5) return { min: 1, max: 10 };
    if (age <= 7) return { min: 1, max: 50 };
    if (age <= 9) return { min: 1, max: 100 };
    return { min: 1, max: 500 };
  }

  private generateMathQuestion(): MathQuestion {
    const range = this.getAgeRange();
    const isAddition = Math.random() > 0.5;

    let a: number;
    let b: number;
    let answer: number;

    if (isAddition) {
      a = Phaser.Math.Between(range.min, Math.min(range.max, 250));
      b = Phaser.Math.Between(range.min, Math.min(range.max, 250));
      answer = a + b;
    } else {
      a = Phaser.Math.Between(range.min + 1, range.max);
      b = Phaser.Math.Between(range.min, a - 1);
      answer = a - b;
    }

    const operator = isAddition ? '+' : '−';
    const display = `${a} ${operator} ${b} = ?`;

    // Generate 4 options including the correct one
    const options = new Set<number>();
    options.add(answer);

    while (options.size < 4) {
      const offset = Phaser.Math.Between(1, Math.max(5, Math.floor(answer * 0.4)));
      const wrong = answer + (Math.random() > 0.5 ? offset : -offset);
      if (wrong >= 0 && wrong !== answer) {
        options.add(wrong);
      }
    }

    // Shuffle
    const shuffled = [...options].sort(() => Math.random() - 0.5);

    return {
      display,
      answer,
      options: shuffled,
    };
  }

  private renderMathChallenge(): void {
    const question = this.generateMathQuestion();
    this.state.question = question;

    // Icon
    this.challengeContainer.add(
      this.add.text(width / 2, 140, '🔢', {
        fontSize: '48px',
      }).setOrigin(0.5),
    );

    // Question title
    this.challengeContainer.add(
      this.add.text(width / 2, 195, 'Matemáticas', {
        fontSize: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#81d4fa',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    // Instructions
    this.challengeContainer.add(
      this.add.text(width / 2, 225, '¿Cuál es el resultado?', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#b0bec5',
      }).setOrigin(0.5),
    );

    // Problem display
    const problemBg = this.add.graphics();
    problemBg.fillStyle(0x283593, 1);
    problemBg.fillRoundedRect(width / 2 - 130, 245, 260, 60, 12);
    problemBg.lineStyle(2, 0x5c6bc0, 1);
    problemBg.strokeRoundedRect(width / 2 - 130, 245, 260, 60, 12);
    this.challengeContainer.add(problemBg);

    this.challengeContainer.add(
      this.add.text(width / 2, 275, question.display, {
        fontSize: '30px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    // Answer options (2×2 grid)
    const cols = 2;
    const optionW = 130;
    const optionH = 54;
    const gap = 16;
    const gridW = cols * optionW + (cols - 1) * gap;
    const startX = width / 2 - gridW / 2 + optionW / 2;
    const startY = 340;

    for (let i = 0; i < question.options.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const optX = startX + col * (optionW + gap);
      const optY = startY + row * (optionH + gap);

      const optBg = this.add.graphics();
      optBg.fillStyle(0x3949ab, 1);
      optBg.fillRoundedRect(optX - optionW / 2, optY - optionH / 2, optionW, optionH, 10);
      this.challengeContainer.add(optBg);

      this.challengeContainer.add(
        this.add.text(optX, optY, String(question.options[i]), {
          fontSize: '26px',
          fontFamily: 'Arial, sans-serif',
          color: '#ffffff',
          fontStyle: 'bold',
        }).setOrigin(0.5),
      );

      const optZone = this.add.zone(optX, optY, optionW, optionH).setInteractive({ useHandCursor: true });
      const idx = i;
      optZone.on('pointerdown', () => {
        this.handleMathAnswer(question.options[idx], question.answer);
      });
      this.challengeContainer.add(optZone);
    }

    // Attempts remaining
    this.challengeContainer.add(
      this.add.text(width / 2, 470, `Intentos restantes: ${this.state.attemptsLeft}`, {
        fontSize: '13px',
        fontFamily: 'Arial, sans-serif',
        color: '#ef9a9a',
      }).setOrigin(0.5),
    );
  }

  private handleMathAnswer(selected: number, correct: number): void {
    if (selected === correct) {
      this.showFeedback(true);
      this.state.challengesCompleted++;
      this.state.attemptsLeft = 0; // prevent further clicks

      // Save math challenge completed
      const profile = getActiveProfile();
      if (profile !== null) {
        addGameResult(profile.id, 0, 0, { mathDone: 1 });
      }

      this.time.delayedCall(1200, () => this.nextChallenge());
    } else {
      this.state.attemptsLeft--;
      this.showFeedback(false);

      if (this.state.attemptsLeft <= 0) {
        // Failed this challenge
        this.time.delayedCall(1200, () => this.handleChallengeFail());
      } else {
        // Refresh to show remaining attempts
        this.time.delayedCall(800, () => this.nextChallenge());
      }
    }
  }

  private renderReadingChallenge(): void {
    // Reading placeholder
    this.challengeContainer.add(
      this.add.text(width / 2, 140, '📖', {
        fontSize: '48px',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, 195, 'Lectura', {
        fontSize: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#a5d6a7',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, height / 2 - 40, 'Próximamente: lectura con voz', {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, height / 2 + 10, 'Por ahora, ¡completa este desafío!', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#b0bec5',
      }).setOrigin(0.5),
    );

    // Auto-complete the reading challenge (always succeeds in placeholder)
    this.time.delayedCall(1500, () => {
      this.showFeedback(true);
      this.state.challengesCompleted++;
      this.time.delayedCall(1000, () => this.nextChallenge());
    });
  }

  private renderWritingChallenge(): void {
    // Writing placeholder
    this.challengeContainer.add(
      this.add.text(width / 2, 140, '✏️', {
        fontSize: '48px',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, 195, 'Escritura', {
        fontSize: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#fff59d',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, height / 2 - 40, 'Próximamente: escritura guiada', {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
      }).setOrigin(0.5),
    );

    this.challengeContainer.add(
      this.add.text(width / 2, height / 2 + 10, 'Por ahora, ¡completa este desafío!', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#b0bec5',
      }).setOrigin(0.5),
    );

    // Auto-complete the writing challenge (always succeeds in placeholder)
    this.time.delayedCall(1500, () => {
      this.showFeedback(true);
      this.state.challengesCompleted++;
      this.time.delayedCall(1000, () => this.nextChallenge());
    });
  }

  private showFeedback(correct: boolean): void {
    this.feedbackContainer.removeAll(true);

    const text = correct ? '✅ ¡Correcto!' : '❌ Incorrecto';
    const color = correct ? '#4caf50' : '#ef5350';

    const bg = this.add.graphics();
    bg.fillStyle(correct ? 0x1b5e20 : 0xb71c1c, 0.9);
    bg.fillRoundedRect(width / 2 - 120, height / 2 - 30, 240, 60, 12);
    bg.lineStyle(2, correct ? 0x4caf50 : 0xef5350, 1);
    bg.strokeRoundedRect(width / 2 - 120, height / 2 - 30, 240, 60, 12);
    this.feedbackContainer.add(bg);

    const feedbackText = this.add.text(width / 2, height / 2, text, {
      fontSize: '24px',
      fontFamily: 'Arial, sans-serif',
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.feedbackContainer.add(feedbackText);
  }

  private handleChallengeFail(): void {
    // Player failed a challenge - end the game session
    this.feedbackContainer.removeAll(true);

    const bg = this.add.graphics();
    bg.fillStyle(0xb71c1c, 0.9);
    bg.fillRoundedRect(width / 2 - 150, height / 2 - 40, 300, 80, 14);
    this.feedbackContainer.add(bg);

    this.feedbackContainer.add(
      this.add.text(width / 2, height / 2 - 10, '❌ Desafío fallido', {
        fontSize: '22px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.feedbackContainer.add(
      this.add.text(width / 2, height / 2 + 20, 'Volviendo al menú principal...', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffcdd2',
      }).setOrigin(0.5),
    );

    this.time.delayedCall(2000, () => {
      this.scene.start('MenuScene');
    });
  }

  private returnToGame(): void {
    this.feedbackContainer.removeAll(true);

    const bg = this.add.graphics();
    bg.fillStyle(0x1b5e20, 0.9);
    bg.fillRoundedRect(width / 2 - 160, height / 2 - 50, 320, 100, 14);
    this.feedbackContainer.add(bg);

    this.feedbackContainer.add(
      this.add.text(width / 2, height / 2 - 15, '🎉 ¡Desafíos completados!', {
        fontSize: '22px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.feedbackContainer.add(
      this.add.text(width / 2, height / 2 + 20, `+${this.state.totalChallenges} intentos extra`, {
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif',
        color: '#a5d6a7',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.time.delayedCall(2000, () => {
      this.scene.start('GameScene', {
        profileId: getActiveProfile()?.id,
        extraAttempts: this.state.totalChallenges,
      });
    });
  }
}